import React from 'react';
import { Route, RouteComponentProps } from 'react-router-dom';
import Wahanda from 'common/wahanda';
import { Reconnect } from 'src/federation/Reconnect';
import type { SharedProps, ValidComponent } from 'reconnect-types/moduleFederation';

import REPORT_TYPE from '../common/reportTypes';
import type { ReportType } from '../common/reportTypes';
import { RECONNECT_REPORT_FLAGS } from './reconnectReportFlags';
import type { ChannelReportFilterChange } from './salesReportPaths';

export { RECONNECT_REPORT_FLAGS };

type RouteMatch = {
  params: {
    venueId: string;
    fromDate?: string;
    toDate?: string;
    employeeFilter?: string;
    selectedChannel?: string;
  };
};

const isReportType = (report: string): report is ReportType =>
  Object.values(REPORT_TYPE).some((value) => value === report);

export const isReconnectSalesReport = (report: string | undefined): boolean => {
  if (!report || !isReportType(report)) {
    return false;
  }
  const flag = RECONNECT_REPORT_FLAGS[report];
  return Boolean(flag && Wahanda.Features.isEnabled(flag));
};

export const parseRouteEmployeeFilter = (employeeFilter?: string): number | undefined => {
  if (!employeeFilter) {
    return undefined;
  }
  const parsed = Number(employeeFilter);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const reconnectReportFilterProps = ({ params }: RouteMatch) => ({
  fromDate: params.fromDate,
  toDate: params.toDate,
  employeeFilter: parseRouteEmployeeFilter(params.employeeFilter),
});

type ReportHistory = {
  push: (path: string) => void;
  replace: (path: string) => void;
};

// The page sets `replace` when it fills in filters missing from the URL on load; replacing the
// entry keeps Back from landing on the incomplete URL, which would fill it in and push again.
export const reportRouteProps = <F,>(
  match: RouteMatch,
  buildPath: (venueId: string, filters: F) => string,
  history: ReportHistory,
) => ({
  ...reconnectReportFilterProps(match),
  onFiltersChange: (filters: F, options?: { replace?: boolean }) => {
    const path = buildPath(match.params.venueId, filters);
    if (options?.replace) {
      history.replace(path);
    } else {
      history.push(path);
    }
  },
});

export const channelReportProps = (
  match: RouteMatch,
  buildPath: (venueId: string, filters: ChannelReportFilterChange) => string,
  history: ReportHistory,
) => ({
  ...reportRouteProps(match, buildPath, history),
  bookingChannel: match.params.selectedChannel,
});

export const reconnectOrLegacyRoute = <C extends ValidComponent>({
  report,
  path,
  name,
  LegacyComponent,
  getProps,
}: {
  report: ReportType;
  path: string;
  name: C;
  LegacyComponent: React.ComponentType<RouteComponentProps<any>> | React.ComponentType<any>;
  getProps: (match: RouteMatch) => SharedProps[C];
}) =>
  isReconnectSalesReport(report) ? (
    <Route
      exact
      path={path}
      render={({ match }) => <Reconnect name={name} props={getProps(match as RouteMatch)} />}
    />
  ) : (
    <Route exact path={path} component={LegacyComponent} />
  );
